import { useLocation, Link, Navigate } from "react-router-dom";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { CheckCircle2, Calendar, Mail, Copy } from "lucide-react";
import { toast } from "sonner";

const StatusBlock = ({ label, result }) => {
  if (!result) return null;
  return (
    <div className="bg-navy-light border border-navy/10 rounded-lg p-5" data-testid={`confirmation-${label.toLowerCase()}`}>
      <p className="text-xs font-bold uppercase tracking-widest text-navy/60">{label} Pathway</p>
      <p className="mt-1 font-display text-lg font-bold text-navy">{result.status}</p>
      {typeof result.score === "number" && (
        <p className="mt-1 text-sm text-magenta font-bold">Preliminary Score: {result.score}/100</p>
      )}
      {result.recommendation && <p className="mt-2 text-sm text-dark-text/75 leading-relaxed">{result.recommendation}</p>}
      {result.recommend_programs && (
        <ul className="mt-2 text-sm text-dark-text/75 list-disc pl-5">
          {result.recommend_programs.map((p) => <li key={p}>{p}</li>)}
        </ul>
      )}
    </div>
  );
};

export default function Confirmation() {
  const { state } = useLocation();
  if (!state) return <Navigate to="/intake" replace />;

  const copyRef = () => {
    navigator.clipboard.writeText(state.reference_number);
    toast.success("Reference number copied");
  };

  return (
    <div className="App bg-navy-light min-h-screen">
      <Header />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <div className="bg-white rounded-lg border border-navy/10 shadow-sm p-8 md:p-10 text-center animate-fade-up">
          <span className="w-16 h-16 mx-auto rounded-full bg-magenta-light flex items-center justify-center mb-5">
            <CheckCircle2 className="text-magenta" size={36} />
          </span>
          <h1 className="font-display text-3xl font-black text-navy">Thank you, {state.name}!</h1>
          <p className="mt-3 text-dark-text/75">Your intake has been received and saved. An AAWTA Enrollment Specialist will review your submission.</p>

          <div className="mt-6 bg-navy text-white rounded-lg p-5 flex items-center justify-between" data-testid="confirmation-reference">
            <div className="text-left">
              <p className="text-xs uppercase tracking-widest text-white/60">Reference Number</p>
              <p className="font-display text-2xl font-black text-magenta">{state.reference_number}</p>
            </div>
            <button onClick={copyRef} className="p-2 rounded-md bg-white/10 hover:bg-white/20 transition-colors" data-testid="confirmation-copy-ref">
              <Copy size={18} />
            </button>
          </div>

          <div className="mt-4 flex flex-wrap justify-center gap-2">
            {state.programs.map((p) => (
              <span key={p} className="bg-magenta-light text-magenta border border-magenta/20 text-xs font-bold px-3 py-1 rounded-full">{p}</span>
            ))}
          </div>
        </div>

        <div className="mt-6 space-y-4">
          <StatusBlock label="CDL" result={state.cdl_result} />
          <StatusBlock label="Dispatcher" result={state.dispatcher_result} />
          <StatusBlock label="WGU" result={state.wgu_result} />
        </div>

        <div className="mt-6 bg-magenta-light border border-magenta/20 rounded-lg p-5 text-sm text-navy">
          Final program approval requires AAWTA and partner review. Acceptance, tuition, funding, employment, or CDL licensing is not guaranteed.
        </div>

        <a href="https://aawta-hub.emergent.host/book/aawta" target="_blank" rel="noopener noreferrer"
           data-testid="confirmation-schedule"
           className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-magenta hover:bg-magenta-dark text-white font-bold py-4 px-6 rounded-md transition-colors">
          <Calendar size={18} /> Schedule With an Enrollment Specialist
        </a>

        <p className="mt-6 text-center text-sm text-dark-text/70">
          <Mail className="inline text-magenta mr-1" size={15} />
          Have additional questions? Email <a href="mailto:contact@aawta.org?subject=Driven%20Together%20Education%20Program" className="text-magenta font-semibold">contact@aawta.org</a> with subject line <strong>Driven Together Education Program</strong>.
        </p>

        <div className="mt-6 text-center">
          <Link to="/" className="text-navy font-semibold hover:text-magenta transition-colors">Return Home</Link>
        </div>
      </div>
      <Footer />
    </div>
  );
}
